import React, { useState } from "react";
import { 
    Box, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Typography, 
    Chip, 
    CircularProgress, 
    Button, 
    IconButton, 
    Snackbar, 
    Alert,
    Avatar
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export default function CampaignDetailsItemsTable({
    items,
    hasMoreItems,
    isLoadingItems,
    isLoadingMore,
    totalItems,
    formatCurrency,
    getStatusColor,
    onLoadMore
}) {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [copiedText, setCopiedText] = useState('');

    const handleCopyMeliId = async (meliId) => {
        try {
            await navigator.clipboard.writeText(meliId);
            setCopiedText(meliId);
            setSnackbarOpen(true);
        } catch (err) {
            console.error('Erro ao copiar MELI_ID:', err);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 600, color: 'grey.700', width: 80 }}>
                                Imagem
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                                Produto
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'grey.700', width: 150 }}>
                                MELI_ID
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'grey.700', width: 120 }}>
                                Preço
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'grey.700', width: 120 }}>
                                Preço Original
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'grey.700', width: 100 }}>
                                Desconto
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'grey.700', width: 100 }}>
                                CMV
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'grey.700', width: 100 }}>
                                Status
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => {
                            const hasOriginalPrice = item.original_price && item.original_price > item.price;
                            const discountPercent = hasOriginalPrice
                                ? Math.round(((item.original_price - item.price) / item.original_price) * 100)
                                : item.discount_amount || null;
                            const imageUrl = item.images && item.images.filter(img => img.main)[0]?.thumbnailUrl;
                            const cmvPrice = item.skus?.[0]?.price || item.skus?.price || item.cmv_price;

                            return (
                                <TableRow 
                                    key={item.id || item.item_id}
                                    hover
                                    sx={{ 
                                        '&:hover': { 
                                            bgcolor: 'rgba(52, 131, 250, 0.02)' 
                                        } 
                                    }}
                                >
                                    <TableCell>
                                        {imageUrl ? (
                                            <Avatar
                                                src={imageUrl}
                                                alt={item.title || item.name || 'Item'}
                                                variant="rounded"
                                                sx={{ 
                                                    width: 60, 
                                                    height: 60, 
                                                    bgcolor: 'grey.100' 
                                                }}
                                            />
                                        ) : (
                                            <Avatar
                                                variant="rounded"
                                                sx={{ 
                                                    width: 60, 
                                                    height: 60, 
                                                    bgcolor: 'grey.100',
                                                    color: 'grey.400'
                                                }}
                                            >
                                                <ShoppingCartIcon />
                                            </Avatar>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                maxWidth: 250,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                lineHeight: 1.4
                                            }}
                                        >
                                            {item.title || item.name || 'Sem título'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary"
                                                sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                                            >
                                                {item.sku || item.item_id || item.id || 'N/A'}
                                            </Typography>
                                            {(item.sku || item.item_id || item.id) && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCopyMeliId(item.sku || item.item_id || item.id)}
                                                    sx={{
                                                        p: 0.5,
                                                        color: 'grey.500',
                                                        '&:hover': {
                                                            color: '#3483fa',
                                                            bgcolor: 'rgba(52, 131, 250, 0.1)'
                                                        }
                                                    }}
                                                >
                                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#2e7d32',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            {formatCurrency(item.price)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {hasOriginalPrice ? (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    textDecoration: 'line-through',
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                {formatCurrency(item.original_price)}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                —
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {discountPercent ? (
                                            <Chip
                                                label={`${discountPercent}% OFF`}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    height: 24,
                                                    bgcolor: '#4caf50',
                                                    color: 'white'
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                —
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {cmvPrice ? (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: '0.8rem' }}
                                            >
                                                {formatCurrency(cmvPrice)}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                —
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={item.status || 'Ativo'}
                                            size="small"
                                            color={getStatusColor(item.status)}
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: '0.7rem',
                                                height: 24
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Loading e ações */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
                minHeight: '80px',
                borderTop: items.length > 0 ? '1px solid' : 'none',
                borderColor: 'grey.100',
                bgcolor: 'grey.25'
            }}>
                {hasMoreItems && !isLoadingItems && !isLoadingMore && (
                    <Button
                        variant="contained"
                        onClick={onLoadMore}
                        disabled={isLoadingItems || isLoadingMore}
                        sx={{
                            bgcolor: '#3483fa',
                            '&:hover': { bgcolor: '#2968c8' },
                            borderRadius: 3,
                            px: 6,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            boxShadow: '0 4px 12px rgba(52, 131, 250, 0.3)',
                            '&:hover': {
                                bgcolor: '#2968c8',
                                boxShadow: '0 6px 16px rgba(52, 131, 250, 0.4)',
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        Carregar mais itens
                    </Button>
                )}
                {(isLoadingItems || isLoadingMore) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={28} sx={{ color: '#3483fa' }} />
                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Carregando mais itens...
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Mensagem de conclusão */}
            {!hasMoreItems && items.length > 0 && (
                <Box sx={{
                    textAlign: 'center',
                    mt: 2,
                    mb: 3,
                    p: 3,
                    bgcolor: 'success.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'success.200'
                }}>
                    <Typography variant="body1" color="success.dark" sx={{ fontWeight: 600 }}>
                        ✨ Todos os {totalItems} itens foram carregados com sucesso!
                    </Typography>
                    <Typography variant="body2" color="success.dark" sx={{ mt: 0.5, opacity: 0.8 }}>
                        {`Você visualizou todos os itens disponíveis nesta campanha.`}
                    </Typography>
                </Box>
            )}

            {/* Snackbar para MELI_ID copiado */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="success"
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    MELI_ID copiado: {copiedText}
                </Alert>
            </Snackbar>
        </Box>
    );
}
